import { useCallback, useEffect, useRef, useState } from "react";
import { IcButton, IcStatusTag, IcAlert } from "@ukic/react";

import { api, type DecodersInfo, type DecoderPackage } from "../api";
import { humanBytes } from "../format";
import { ConfirmDialog } from "./ConfirmDialog";
import { useToast } from "../toast";

/**
 * Per-device "Decoder packages" panel (web-managed install/remove). Optional
 * decoders are built ON the device (source/apt), so each box decides which
 * capabilities to carry — a real memory/storage trade-off, shown here against
 * the device's free disk. Install/Remove are admin-only and run detached on the
 * device; while an action runs we poll for progress.
 */
export function DecoderPackages({
  deviceId,
  isAdmin,
  deviceActive,
}: {
  deviceId: string;
  isAdmin: boolean;
  deviceActive: boolean;
}) {
  const toast = useToast();
  const [info, setInfo] = useState<DecodersInfo | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<{ action: "install" | "remove"; pkg: DecoderPackage } | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(() => {
    api.getDecoders(deviceId).then((d) => { setInfo(d); setError(null); }).catch(setError);
  }, [deviceId]);

  // Poll while an install/remove is running, otherwise just load once.
  useEffect(() => {
    if (!deviceActive) return;
    load();
  }, [load, deviceActive]);

  useEffect(() => {
    const running = info?.action?.running;
    if (running && timer.current == null) {
      timer.current = window.setInterval(load, 4000);
    } else if (!running && timer.current != null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
    return () => {
      if (timer.current != null) { window.clearInterval(timer.current); timer.current = null; }
    };
  }, [info?.action?.running, load]);

  const run = useCallback(async () => {
    if (!pending) return;
    const { action, pkg } = pending;
    setPending(null);
    try {
      await api.manageDecoders(deviceId, action, pkg.name);
      toast.success(
        action === "install" ? "Install started" : "Remove started",
        `${pkg.title} — running on the device; progress updates below.`,
      );
      load();
    } catch (e) {
      toast.error("Action failed", e instanceof Error ? e.message : String(e));
    }
  }, [pending, deviceId, toast, load]);

  if (!deviceActive) return null;

  const act = info?.action;
  const busy = !!act?.running;
  const disk = info?.disk;

  return (
    <div className="hl-panel hl-stack">
      <h2>Decoder packages</h2>
      <p className="hl-muted">
        Optional decoders are built on the device, so each box only carries the
        capabilities you enable.{" "}
        {disk && (
          <>Disk: <strong>{humanBytes(disk.free)}</strong> free of {humanBytes(disk.total)}.</>
        )}
      </p>

      {error != null && (
        <IcAlert variant="error" heading="Could not load packages"
          message={error instanceof Error ? error.message : String(error)} />
      )}

      {busy && (
        <>
          <IcAlert variant="info"
            heading={`${act?.op === "remove" ? "Removing" : "Installing"} ${act?.package}…`}
            message="This runs on the device and can take several minutes (TETRA pulls in GNU Radio)." />
          {act?.log_tail && <pre className="hl-log">{act.log_tail}</pre>}
        </>
      )}

      <div className="hl-stack">
        {(info?.packages ?? []).map((p) => (
          <div key={p.name} className="hl-row hl-pkg-row">
            <div className="hl-pkg-info">
              <strong>{p.title}</strong>{" "}
              <IcStatusTag
                className="hl-inline-tag"
                size="small"
                label={p.installed ? "installed" : "not installed"}
                status={p.installed ? "success" : "neutral"}
              />
              <div className="hl-muted">
                {p.note} · formats: {p.formats.join(", ")} · ~{p.size_mb} MB
              </div>
            </div>
            {isAdmin && (
              <div className="hl-row">
                {!p.installed ? (
                  <IcButton size="small" disabled={busy}
                    onClick={() => setPending({ action: "install", pkg: p })}>
                    Install
                  </IcButton>
                ) : (
                  <IcButton size="small" variant="destructive" disabled={busy}
                    onClick={() => setPending({ action: "remove", pkg: p })}>
                    Remove
                  </IcButton>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={pending !== null}
        heading={pending?.action === "remove" ? "Remove decoder package?" : "Install decoder package?"}
        message={
          pending == null
            ? ""
            : pending.action === "install"
              ? `Build and install ${pending.pkg.title} (~${pending.pkg.size_mb} MB) on the device. ` +
                (pending.pkg.name === "tetra"
                  ? "This pulls in GNU Radio and can take ~10–15 min on a Pi."
                  : "This compiles from source and can take a few minutes.")
              : `Remove ${pending.pkg.title} and reclaim ~${pending.pkg.size_mb} MB. ` +
                (pending.pkg.name === "tetra"
                  ? "GNU Radio + gr-osmosdr will be purged."
                  : "Its binary will be deleted.")
        }
        destructive={pending?.action === "remove"}
        onConfirm={run}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
